import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { ArrowLeft, ExternalLink, Plus, Trash2, Loader2, Link as LinkIcon, Upload, Image, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FB_PAGE_URL = 'https://www.facebook.com/ccamontreal26';

const FacebookIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
);

/* Load Facebook SDK once (best-effort for public posts) */
const loadFacebookSDK = (() => {
  let loaded = false;
  let loading = false;
  const callbacks = [];
  return () => new Promise((resolve) => {
    if (loaded) { resolve(true); return; }
    callbacks.push(resolve);
    if (loading) return;
    loading = true;
    window.fbAsyncInit = () => {
      window.FB.init({ xfbml: false, version: 'v21.0' });
      loaded = true;
      callbacks.forEach(cb => cb(true));
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => { callbacks.forEach(cb => cb(false)); };
    document.body.appendChild(script);
  });
})();

/* Extract a readable page name from URL */
const getPostLabel = (url) => {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    const pageName = parts[0] || 'Post';
    return pageName.replace(/[._-]/g, ' ');
  } catch { return 'Facebook Post'; }
};

/* Individual Facebook Post — card with optional SDK embed overlay */
const PostEmbed = ({ url }) => {
  const embedRef = useRef(null);
  const [sdkRendered, setSdkRendered] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tryEmbed = async () => {
      const sdkOk = await loadFacebookSDK();
      if (!sdkOk || cancelled || !embedRef.current || !window.FB) return;
      window.FB.XFBML.parse(embedRef.current, () => {
        if (cancelled) return;
        // Check if SDK actually rendered a visible iframe
        setTimeout(() => {
          const iframe = embedRef.current?.querySelector('iframe');
          if (iframe && iframe.offsetHeight > 50) {
            setSdkRendered(true);
          }
        }, 2000);
      });
    };
    tryEmbed();
    return () => { cancelled = true; };
  }, [url]);

  const pageName = getPostLabel(url);

  return (
    <div className="break-inside-avoid mb-4" data-testid="fb-post-embed">
      {/* SDK embed — hidden by default, shown only if actually rendered */}
      <div ref={embedRef} style={{ display: sdkRendered ? 'block' : 'none' }}>
        <div className="fb-post" data-href={url} data-width="350" data-show-text="true" data-lazy="true" />
      </div>

      {/* Primary display — styled card with link to Facebook */}
      {!sdkRendered && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all group"
        >
          <div className="bg-gradient-to-br from-[#1877F2] to-[#0a5dc2] p-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <FacebookIcon className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm truncate capitalize">{pageName}</p>
                <p className="text-white/60 text-xs">Community Post</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <p className="text-gray-500 text-xs mb-3 truncate">{url.replace('https://www.', '').replace('https://', '')}</p>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-[#1877F2] text-sm font-semibold group-hover:underline">
                View Post <ExternalLink className="w-3.5 h-3.5" />
              </span>
              <span className="text-gray-300 text-[10px]">facebook.com</span>
            </div>
          </div>
        </a>
      )}
    </div>
  );
};

/* Gallery Photo Card */
const PhotoCard = ({ photo, onDelete, isAdmin }) => (
  <div className="break-inside-avoid mb-4 group relative" data-testid="gallery-photo">
    <div className="rounded-lg overflow-hidden bg-white/5 border border-white/10">
      <img
        src={photo.url.startsWith('/') ? `${BACKEND_URL}${photo.url}` : photo.url}
        alt={photo.caption || 'CCA Festival Photo'}
        className="w-full object-cover"
        loading="lazy"
      />
      {photo.caption && (
        <div className="p-2">
          <p className="text-white/70 text-xs">{photo.caption}</p>
        </div>
      )}
    </div>
    {isAdmin && onDelete && (
      <button onClick={() => onDelete(photo._id)} className="absolute top-2 right-2 bg-red-600/90 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${photo._id}`}>
        <X className="w-3 h-3" />
      </button>
    )}
  </div>
);

const GalleryPage = () => {
  const [fbPosts, setFbPosts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [tab, setTab] = useState('photos'); // 'photos' | 'facebook'

  const isAdmin = !!sessionStorage.getItem('cca_token') || !!localStorage.getItem('cca_token');

  const fetchData = useCallback(async () => {
    try {
      const [fbRes, photoRes] = await Promise.all([
        axios.get(`${API_URL}/facebook-posts`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gallery-photos`).catch(() => ({ data: [] })),
      ]);
      setFbPosts(fbRes.data);
      setPhotos(photoRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addFbPost = async () => {
    if (!newUrl.trim() || !newUrl.includes('facebook.com')) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/facebook-posts`, { url: newUrl.trim() });
      setNewUrl('');
      fetchData();
    } catch { /* ok */ }
    finally { setAdding(false); }
  };

  const deleteFbPost = async (id) => {
    try { await axios.delete(`${API_URL}/facebook-posts/${id}`); setFbPosts(p => p.filter(x => x._id !== id)); } catch { /* ok */ }
  };

  const uploadPhoto = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('caption', '');
        await axios.post(`${API_URL}/gallery-photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      fetchData();
    } catch { /* ok */ }
    finally { setUploading(false); e.target.value = ''; }
  };

  const deletePhoto = async (id) => {
    try { await axios.delete(`${API_URL}/gallery-photos/${id}`); setPhotos(p => p.filter(x => x._id !== id)); } catch { /* ok */ }
  };

  const hasContent = photos.length > 0 || fbPosts.length > 0;

  return (
    <div className="min-h-screen bg-slate-900">
      <div id="fb-root" />
      <Navbar />

      {/* Hero */}
      <section className="relative py-10 sm:py-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cameroon-green/10 via-transparent to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-3" data-testid="gallery-title">
            CCA Montreal Gallery
          </h1>
          <p className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto mb-5">
            Photos, posts, and moments from the Council of Cultural Associations Montreal.
          </p>
          <a href={FB_PAGE_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2] hover:bg-[#1877F2]/90 text-white text-sm transition-colors" data-testid="fb-page-link">
            <FacebookIcon className="w-4 h-4" /> Follow us on Facebook <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>

      {/* Admin Panel */}
      {isAdmin && (
        <section className="pb-4">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <button onClick={() => setShowAdmin(!showAdmin)} className="flex items-center gap-2 text-cameroon-yellow text-xs hover:text-cameroon-yellow/80 transition-colors mb-3" data-testid="admin-toggle">
              <Plus className="w-3 h-3" /> {showAdmin ? 'Hide Admin Panel' : 'Manage Gallery (Admin)'}
            </button>
            {showAdmin && (
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 space-y-4" data-testid="admin-panel">
                {/* Photo Upload */}
                <div>
                  <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2"><Image className="w-4 h-4 text-cameroon-green" /> Upload Photos</h3>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-white/20 hover:border-cameroon-green/50 cursor-pointer transition-colors" data-testid="photo-upload-area">
                    {uploading ? <Loader2 className="w-5 h-5 text-cameroon-yellow animate-spin" /> : <Upload className="w-5 h-5 text-white/40" />}
                    <span className="text-white/60 text-xs">{uploading ? 'Uploading...' : 'Click to upload festival photos (multiple allowed)'}</span>
                    <input type="file" accept="image/*" multiple onChange={uploadPhoto} className="hidden" disabled={uploading} data-testid="photo-upload-input" />
                  </label>
                </div>

                {/* Facebook Post URLs */}
                <div>
                  <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2"><FacebookIcon className="w-4 h-4 text-[#1877F2]" /> Embed Facebook Posts</h3>
                  <div className="bg-[#1877F2]/5 border border-[#1877F2]/20 rounded-lg p-3 mb-2">
                    <p className="text-white/60 text-xs leading-relaxed">
                      <strong className="text-white/80">How to get a post URL:</strong> Go to your
                      <a href={FB_PAGE_URL} target="_blank" rel="noopener noreferrer" className="text-[#1877F2] mx-1">Facebook page</a>
                      → click on a specific post → copy the URL from your browser address bar. It should look like:
                      <code className="text-cameroon-yellow text-[10px] block mt-1 break-all">facebook.com/ccamontreal26/posts/12345678...</code>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="https://www.facebook.com/ccamontreal26/posts/..." value={newUrl} onChange={e => setNewUrl(e.target.value)}
                      className="bg-white/5 border-white/10 text-white text-sm flex-1" data-testid="add-post-input" />
                    <Button onClick={addFbPost} disabled={adding || !newUrl.includes('facebook.com')} className="bg-[#1877F2] hover:bg-[#1877F2]/90" data-testid="add-post-btn">
                      {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                  {fbPosts.length > 0 && (
                    <div className="space-y-1.5 mt-2 max-h-32 overflow-y-auto">
                      {fbPosts.map(p => (
                        <div key={p._id} className="flex items-center gap-2 text-xs">
                          <LinkIcon className="w-3 h-3 text-white/30 flex-shrink-0" />
                          <span className="text-white/60 truncate flex-1">{p.url}</span>
                          <button onClick={() => deleteFbPost(p._id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Content Tabs + Gallery */}
      <section className="pb-12 sm:pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Tab toggle (only show if both types of content exist) */}
          {photos.length > 0 && fbPosts.length > 0 && (
            <div className="flex justify-center mb-6">
              <div className="inline-flex rounded-lg overflow-hidden border border-white/10">
                <button onClick={() => setTab('photos')} className={`px-4 py-2 text-xs font-medium transition-colors ${tab === 'photos' ? 'bg-cameroon-green text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-testid="tab-photos">
                  <Image className="w-3.5 h-3.5 inline mr-1.5" />Photos ({photos.length})
                </button>
                <button onClick={() => setTab('facebook')} className={`px-4 py-2 text-xs font-medium transition-colors ${tab === 'facebook' ? 'bg-[#1877F2] text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-testid="tab-facebook">
                  <FacebookIcon className="w-3.5 h-3.5 inline mr-1.5" />Facebook Posts ({fbPosts.length})
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cameroon-yellow animate-spin" /></div>
          ) : !hasContent ? (
            /* Empty state */
            <div className="text-center py-12 bg-white/5 rounded-2xl border border-white/10" data-testid="empty-state">
              <Image className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/50 text-sm mb-2">Gallery is being prepared</p>
              <p className="text-white/30 text-xs mb-4">Photos and posts from our festivals will appear here soon.</p>
              <a href={FB_PAGE_URL} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2] hover:bg-[#1877F2]/90 text-white text-sm transition-colors">
                <FacebookIcon className="w-4 h-4" /> Visit our Facebook Page
              </a>
            </div>
          ) : (
            <>
              {/* Photos Grid (masonry) */}
              {(tab === 'photos' || fbPosts.length === 0) && photos.length > 0 && (
                <div className="columns-2 sm:columns-3 lg:columns-4 gap-3" data-testid="photos-grid">
                  {photos.map(p => <PhotoCard key={p._id} photo={p} onDelete={deletePhoto} isAdmin={isAdmin} />)}
                </div>
              )}

              {/* Facebook Posts Grid */}
              {(tab === 'facebook' || photos.length === 0) && fbPosts.length > 0 && (
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="fb-posts-grid">
                  {fbPosts.map(p => <PostEmbed key={p._id} url={p.url} />)}
                </div>
              )}
            </>
          )}

          {/* Quick Links */}
          <div className="grid sm:grid-cols-3 gap-3 mt-8">
            <a href={`${FB_PAGE_URL}/photos`} target="_blank" rel="noopener noreferrer"
              className="group bg-white/5 rounded-xl border border-white/10 p-4 hover:bg-white/10 transition-all" data-testid="fb-photos-card">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#1877F2]/20 flex items-center justify-center"><FacebookIcon className="w-4 h-4" /></div>
                <div><h3 className="text-white font-semibold text-xs">All Photos</h3><span className="text-[#1877F2] text-[10px]">View on Facebook</span></div>
              </div>
            </a>
            <a href={`${FB_PAGE_URL}/videos`} target="_blank" rel="noopener noreferrer"
              className="group bg-white/5 rounded-xl border border-white/10 p-4 hover:bg-white/10 transition-all" data-testid="fb-videos-card">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-cameroon-red/20 flex items-center justify-center"><svg className="w-4 h-4 text-cameroon-red" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg></div>
                <div><h3 className="text-white font-semibold text-xs">Videos</h3><span className="text-cameroon-red text-[10px]">Watch highlights</span></div>
              </div>
            </a>
            <a href={FB_PAGE_URL} target="_blank" rel="noopener noreferrer"
              className="group bg-white/5 rounded-xl border border-white/10 p-4 hover:bg-white/10 transition-all" data-testid="fb-follow-card">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-cameroon-green/20 flex items-center justify-center"><FacebookIcon className="w-4 h-4 text-cameroon-green" /></div>
                <div><h3 className="text-white font-semibold text-xs">Follow Us</h3><span className="text-cameroon-green text-[10px]">Stay updated</span></div>
              </div>
            </a>
          </div>

          <div className="mt-8 text-center">
            <Link to="/"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="back-home-btn"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Home</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
